import React, { useState, useCallback, useEffect } from 'react';
import classNames from 'classnames';
import { IconButton } from '../buttons/IconButton';
import './DateRangeFilterLayer.less';
import { getMoment } from 'utils/DateTimeUtils';
import { CalendarRange } from 'components/inputs/date-time/CalendarRange';
import { CustomDateRangeFilter } from './CustomDateRangeFilter';
import { DateRangeTypes } from '@decisions/server/DecisionsFramework/Design/Flow/CoreSteps/Metro/DateRangeTypes';
import {
  ReportDateRange,
  newReportDateRange,
} from '@decisions/server/DecisionsFramework/Design/Flow/CoreSteps/Metro/ReportDateRange';
import moment from 'moment';

export interface IDateRangeFilterLayerProps {
  dateRangeFilterData?: ReportDateRange;
  disableRollingRange?: boolean;
  onSelectionChange: (dateRangeFilterData: ReportDateRange) => void;
}

export function DateRangeFilterLayer({
  dateRangeFilterData: initialDateRangeFilterData,
  onSelectionChange,
  disableRollingRange,
}: IDateRangeFilterLayerProps) {
  const [dateRangeFilterData, setDateRangeFilterData] = useState(initialDateRangeFilterData);
  const [dateRangeType, setDateRangeType] = useState(dateRangeFilterData?.DateRange);

  useEffect(() => {
    setDateRangeFilterData(initialDateRangeFilterData);
    setDateRangeType(initialDateRangeFilterData?.DateRange);
  }, [initialDateRangeFilterData]);

  const getDateRange = useCallback(
    (type?: DateRangeTypes): { startDate?: moment.Moment; endDate?: moment.Moment } => {
      switch (type) {
        case DateRangeTypes.Today:
          return { startDate: getMoment()(), endDate: getMoment()() };
        case DateRangeTypes.ThisWeek:
          return { startDate: getMoment()().startOf('week'), endDate: getMoment()().endOf('week') };
        case DateRangeTypes.ThisMonth:
          return { startDate: getMoment()().startOf('month'), endDate: getMoment()().endOf('month') };
        default:
          return {
            startDate: getMoment()(dateRangeFilterData?.FromDate),
            endDate:
              dateRangeFilterData &&
              getMoment()(dateRangeFilterData?.FromDate).add(dateRangeFilterData?.NumberOfDays - 1, 'days'),
          };
      }
    },
    [dateRangeFilterData]
  );

  const onCalendarSelectionChange = useCallback(
    (selectedStartDate?: moment.Moment, selectedEndDate?: moment.Moment) => {
      setDateRange({
        startDate: selectedStartDate,
        endDate: selectedEndDate,
      });
      onSelectionChange &&
        onSelectionChange(
          newReportDateRange({
            ...dateRangeFilterData,
            DateRange: DateRangeTypes.Custom,
            FromDate: selectedStartDate?.toDate(),
            NumberOfDays: (selectedEndDate?.startOf('day').diff(selectedStartDate?.startOf('day'), 'days') || 0) + 1,
          })
        );
    },
    [dateRangeFilterData, onSelectionChange]
  );

  const [dateRange, setDateRange] = useState(getDateRange(dateRangeType));
  const startDate = dateRange?.startDate;
  const endDate = dateRange?.endDate;

  useEffect(() => {
    setDateRangeType(dateRangeFilterData?.DateRange);
  }, [dateRangeFilterData]);

  useEffect(() => {
    setDateRange(getDateRange(dateRangeType));
  }, [dateRangeType, getDateRange]);

  const shouldSelectOther = useCallback(
    (type: DateRangeTypes) => {
      return (
        dateRangeType &&
        type === DateRangeTypes.Other &&
        [DateRangeTypes.Today, DateRangeTypes.ThisWeek, DateRangeTypes.ThisMonth, DateRangeTypes.None].includes(
          dateRangeType
        ) === false
      );
    },
    [dateRangeType]
  );

  const choiceButtonClassNames = (type: DateRangeTypes) => {
    return classNames('dpc-date-filter-layer__choice-button', 'dpc-date-filter-layer__choice-button--primary', {
      'dpc-date-filter-layer__choice-button--active': type === dateRangeType || shouldSelectOther(type),
    });
  };

  const setDateRangeChoice = useCallback(
    (selectedType: DateRangeTypes) => {
      setDateRangeType(selectedType);
      const dateFilterData = newReportDateRange({ ...dateRangeFilterData, DateRange: selectedType });
      setDateRangeFilterData(dateFilterData);
      onSelectionChange && onSelectionChange(dateFilterData);
      const dateRange = getDateRange(selectedType);
      setDateRange(dateRange);
    },
    [dateRangeFilterData, getDateRange, onSelectionChange]
  );

  const getLayer = () => {
    if (dateRangeType) {
      if (
        dateRangeType === DateRangeTypes.Today ||
        dateRangeType === DateRangeTypes.ThisWeek ||
        dateRangeType === DateRangeTypes.ThisMonth
      ) {
        return (
          <CalendarRange
            wrapperClass="dpc-date-filter-layer__calendar"
            headerClass="dpc-date-filter-layer__calendar-header"
            startDate={startDate}
            endDate={endDate}
            selectRange={true}
            onRangeChange={onCalendarSelectionChange}
          />
        );
      } else {
        return (
          <CustomDateRangeFilter
            disableRollingRange={disableRollingRange}
            dateRangeFilterData={dateRangeFilterData}
            onSelectionChange={onSelectionChange}
          />
        );
      }
    }
  };

  return (
    <div className="dpc-date-filter-layer">
      <div className="dpc-date-filter-layer__options">
        <IconButton
          title="Today"
          className={choiceButtonClassNames(DateRangeTypes.Today)}
          iconClassName="dpc-date-filter-layer__icon dpc-date-filter-layer__icon--today"
          onClick={() => setDateRangeChoice(DateRangeTypes.Today)}
        >
          <span>Today</span>
        </IconButton>
        <IconButton
          title="This Week"
          className={choiceButtonClassNames(DateRangeTypes.ThisWeek)}
          iconClassName="dpc-date-filter-layer__icon dpc-date-filter-layer__icon--week"
          onClick={() => setDateRangeChoice(DateRangeTypes.ThisWeek)}
        >
          <span>This Week</span>
        </IconButton>
        <IconButton
          title="This Month"
          className={choiceButtonClassNames(DateRangeTypes.ThisMonth)}
          iconClassName="dpc-date-filter-layer__icon dpc-date-filter-layer__icon--month"
          onClick={() => setDateRangeChoice(DateRangeTypes.ThisMonth)}
        >
          <span>This Month</span>
        </IconButton>

        <IconButton
          title="Other"
          className={choiceButtonClassNames(DateRangeTypes.Other)}
          iconClassName="dpc-date-filter-layer__icon dpc-date-filter-layer__icon--other"
          onClick={() => setDateRangeChoice(DateRangeTypes.Other)}
        >
          <span>Other</span>
        </IconButton>
      </div>
      <div className="dpc-date-filter-layer__selected">{getLayer()}</div>
    </div>
  );
}
